import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Typography, Space, Button } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "../../contexts/ThemeContext";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { getSafeExternalUrl } from "../../utils/safeExternalUrl";
import LoginUpdateNoticeAnalyticsService from "../../services/loginUpdateNoticeAnalyticsService";

const { Title } = Typography;

const VERTICAL_DEFAULTS = { imageHeightPct: 0.60, imageHeightMaxPx: 600 };
const HORIZONTAL_DEFAULTS = { imageWidthPct: 0.48 };

function calcVerticalDimensions(naturalWidth, naturalHeight, heightPct, heightMaxPx) {
  const vhCss = window.innerHeight;
  const maxImageH = Math.min(vhCss * heightPct, heightMaxPx);
  const ratio = naturalWidth / naturalHeight;
  const rawW = Math.round(maxImageH * ratio);
  const modalW = Math.max(Math.min(rawW, 800), 420);
  const finalImageH = Math.round(modalW / ratio);
  return { modalW, imageH: finalImageH };
}

// Conteúdo textual compartilhado entre layouts
function NoticeContent({ notice, hasAction, onClose, handleAction, theme, sanitizedDescription }) {
  return (
    <>
      <div>
        <Title level={3} style={{ marginTop: 0, marginBottom: 16, color: theme.textPrimary }}>
          {notice?.title}
        </Title>
        <div
          style={{
            color: theme.textSecondary,
            fontSize: 15,
            lineHeight: 1.75,
            marginBottom: 20,
            textAlign: "justify",
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
        />
      </div>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {hasAction ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleAction}
            style={{ height: 46, borderRadius: 12, fontWeight: 600, borderColor: theme.primary }}
          >
            {notice.action.label}
          </Button>
        ) : null}
        <Button
          type="default"
          block
          size="large"
          onClick={onClose}
          style={{
            height: 46,
            borderRadius: 12,
            fontWeight: 600,
            borderColor: theme.primary,
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.15)",
            transition: "all 0.25s ease",
          }}
        >
          Entendi, prosseguir
        </Button>
      </Space>
    </>
  );
}

const LoginUpdateNoticeModal = ({ open, notice, onClose, onAction, interactionId }) => {
  const { theme } = useTheme();
  const [mediaFailed, setMediaFailed] = useState(false);
  const [dimensions, setDimensions] = useState({ modalW: 680, imageH: null });

  // Evita disparo duplicado do mesmo clique (ex.: bubbling/duplo bind no
  // mesmo evento). É resetado a cada nova exibição da campanha, então
  // cliques legítimos em aberturas futuras do modal continuam funcionando.
  const clickInFlightRef = useRef(false);

  const displayConfig = notice?.displayConfig ?? {};
  const layoutMode = displayConfig.layoutMode || "vertical";
  const heightPct = displayConfig.imageHeightPct ?? VERTICAL_DEFAULTS.imageHeightPct;
  const heightMaxPx = displayConfig.imageHeightMaxPx ?? VERTICAL_DEFAULTS.imageHeightMaxPx;
  const widthPct = displayConfig.imageWidthPct ?? HORIZONTAL_DEFAULTS.imageWidthPct;
  const isHorizontal = layoutMode === "horizontal";

  useEffect(() => {
    setMediaFailed(false);
    clickInFlightRef.current = false;
    setDimensions({ modalW: 680, imageH: null });
  }, [notice?.id]);

  const sanitizedDescription = useMemo(
    () =>
      sanitizeHtml(notice?.descriptionHtml || "", {
        allowedTags: ["P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "A", "H3", "H4"],
      }),
    [notice?.descriptionHtml],
  );

  const media = notice?.media;
  const hasMedia = Boolean(media?.url && !mediaFailed);
  const actionUrl = getSafeExternalUrl(notice?.action?.url);
  const hasAction = Boolean(actionUrl && notice?.action?.label);

  const handleImageLoad = (e) => {
    const { naturalWidth: nw, naturalHeight: nh } = e.target;
    if (!nw || !nh || isHorizontal) return;
    setDimensions(calcVerticalDimensions(nw, nh, heightPct, heightMaxPx));
  };

  const handleVideoLoad = (e) => {
    const { videoWidth: nw, videoHeight: nh } = e.target;
    if (!nw || !nh || isHorizontal) return;
    setDimensions(calcVerticalDimensions(nw, nh, heightPct, heightMaxPx));
  };

  const trackClickOnce = () => {
    if (clickInFlightRef.current) return;
    if (!notice?.id || !interactionId) return;
    clickInFlightRef.current = true;
    const clickEventId = uuidv4();
    LoginUpdateNoticeAnalyticsService.trackClick(notice.id, { eventId: clickEventId, interactionId });
  };

  const handleAction = () => {
    if (!hasAction) return;
    if (onAction) {
      // window.open precisa ocorrer de forma síncrona dentro do handler de
      // clique para não ser bloqueado como popup; o tracking é disparado
      // depois, de forma assíncrona/non-blocking.
      onAction(notice);
      trackClickOnce();
      return;
    }
    window.open(
      actionUrl,
      notice?.action?.openInNewTab === false ? "_self" : "_blank",
      "noopener,noreferrer",
    );
    trackClickOnce();
  };

  const { modalW, imageH } = dimensions;
  const modalWidth = hasMedia ? (isHorizontal ? 860 : modalW) : 520;

  const contentProps = { notice, hasAction, onClose, handleAction, theme, sanitizedDescription };

  return (
    <Modal
      open={open && Boolean(notice)}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={modalWidth}
      style={{ maxWidth: "calc(100vw - 48px)" }}
      footer={null}
      styles={{
        content: {
          borderRadius: 20,
          overflow: "hidden",
          padding: 0,
          background: theme.cardBackground || "#ffffff",
        },
        body: {
          padding: 0,
          maxHeight: "90vh",
          overflowY: isHorizontal ? "hidden" : "auto",
        },
        mask: {
          backgroundColor: "rgba(6, 10, 30, 0.62)",
          backdropFilter: "blur(5px)",
        },
      }}
    >
      {isHorizontal ? (
        // ── LAYOUT HORIZONTAL ──
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          {hasMedia && (
            <div
              style={{
                width: `${widthPct * 100}%`,
                flexShrink: 0,
                overflow: "hidden",
                borderRight: `1px solid ${theme.border}`,
                aspectRatio: "4 / 3",
              }}
            >
              {media.type === "video" ? (
                <video
                  src={media.url}
                  controls
                  playsInline
                  preload="metadata"
                  onError={() => setMediaFailed(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <img
                  src={media.url}
                  alt={media.alt || notice?.title || ""}
                  onError={() => setMediaFailed(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              )}
            </div>
          )}
          <div
            style={{
              flex: 1,
              padding: 28,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowY: "auto",
              maxHeight: "90vh",
            }}
          >
            <NoticeContent {...contentProps} />
          </div>
        </div>
      ) : (
        // ── LAYOUT VERTICAL ──
        <>
          {hasMedia && (
            <div
              style={{
                width: "100%",
                borderBottom: `1px solid ${theme.border}`,
                lineHeight: 0,
              }}
            >
              {media.type === "video" ? (
                <video
                  src={media.url}
                  controls
                  playsInline
                  preload="metadata"
                  onError={() => setMediaFailed(true)}
                  onLoadedMetadata={handleVideoLoad}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              ) : (
                <img
                  src={media.url}
                  alt={media.alt || notice?.title || ""}
                  onError={() => setMediaFailed(true)}
                  onLoad={handleImageLoad}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              )}
            </div>
          )}
          <div style={{ padding: 24 }}>
            <NoticeContent {...contentProps} />
          </div>
        </>
      )}
    </Modal>
  );
};

export default LoginUpdateNoticeModal;
